import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocaleFmt } from "@/lib/i18n/format";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RefreshCw, RotateCcw, Webhook } from "lucide-react";
import { toast } from "sonner";
import { adminListWebhookEvents, adminRetryWebhookEvent } from "@/lib/admin-webhooks.functions";

export const Route = createFileRoute("/_authenticated/app/admin/webhooks")({
  beforeLoad: async () => {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) throw redirect({ to: "/login" });
    const { data: roles } = await supabase.from("user_roles").select("role").eq("user_id", u.user.id);
    if (!roles?.some((r) => r.role === "admin")) throw redirect({ to: "/app" });
  },
  component: WebhooksPage,
});

type Row = {
  id: string;
  source: string;
  event_id: string | null;
  event_type: string;
  environment: string;
  status: string;
  attempts: number;
  error: string | null;
  payload: any;
  created_at: string;
  updated_at: string;
};

function WebhooksPage() {
  const { t } = useTranslation();
  const { localeTag } = useLocaleFmt();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [environment, setEnv] = useState<"all" | "sandbox" | "live">("all");
  const [status, setStatus] = useState<"all" | "received" | "processing" | "processed" | "failed">("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Row | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListWebhookEvents({ data: { environment, status, search: search || undefined, limit: 300 } });
      setRows(data as any);
    } catch (e: any) {
      toast.error(e?.message ?? t("pages.admin.loadError"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [environment, status]);

  const retry = async (id: string) => {
    setRetrying(id);
    try {
      const r = await adminRetryWebhookEvent({ data: { id } });
      if (r.ok) toast.success(t("pages.admin.eventProcessed"));
      else toast.error(t("pages.admin.webhooksPage.retryFailedReason", { reason: r.error ?? t("pages.admin.genericError") }));
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? t("pages.admin.retryFailed"));
    } finally {
      setRetrying(null);
    }
  };

  const stats = {
    total: rows.length,
    failed: rows.filter((r) => r.status === "failed").length,
    processed: rows.filter((r) => r.status === "processed").length,
    processing: rows.filter((r) => r.status === "processing").length,
  };

  return (
    <>
      <PageHeader
        title={t("pages.admin.webhooksPage.title")}
        description={t("pages.admin.webhooksPage.description")}
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm"><Link to="/app/admin">{t("pages.admin.adminBack")}</Link></Button>
            <Button variant="outline" size="sm" onClick={load}><RefreshCw className="mr-2 h-4 w-4" /> {t("pages.admin.reload")}</Button>
          </div>
        }
      />
      <PageBody>
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: t("pages.admin.webhooksPage.totalEvents"), value: stats.total },
            { label: t("pages.admin.webhooksPage.processed"), value: stats.processed },
            { label: t("pages.admin.webhooksPage.processing"), value: stats.processing },
            { label: t("pages.admin.webhooksPage.failed"), value: stats.failed },
          ].map((s) => (
            <Card key={s.label}>
              <CardContent className="flex items-center gap-3 pt-6">
                <Webhook className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="text-2xl font-bold">{s.value}</div>
                  <div className="text-xs text-muted-foreground">{s.label}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              <span>{t("pages.admin.webhooksPage.events")}</span>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && load()}
                  placeholder={t("pages.admin.webhooksPage.searchPh")}
                  className="h-8 w-56"
                />
                <Select value={environment} onValueChange={(v) => setEnv(v as any)}>
                  <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("pages.admin.webhooksPage.allEnv")}</SelectItem>
                    <SelectItem value="sandbox">Sandbox</SelectItem>
                    <SelectItem value="live">Live</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={status} onValueChange={(v) => setStatus(v as any)}>
                  <SelectTrigger className="h-8 w-36"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("pages.admin.webhooksPage.allStatus")}</SelectItem>
                    <SelectItem value="received">received</SelectItem>
                    <SelectItem value="processing">processing</SelectItem>
                    <SelectItem value="processed">processed</SelectItem>
                    <SelectItem value="failed">failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground">{t("pages.admin.loading")}</p>
            ) : (
              <div className="max-h-[65vh] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("pages.admin.cols.when")}</TableHead>
                      <TableHead>{t("pages.admin.cols.type")}</TableHead>
                      <TableHead>{t("pages.admin.cols.env")}</TableHead>
                      <TableHead>{t("pages.admin.cols.status")}</TableHead>
                      <TableHead>{t("pages.admin.cols.attempts")}</TableHead>
                      <TableHead>{t("pages.admin.cols.error")}</TableHead>
                      <TableHead className="text-right">{t("pages.admin.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                          {new Date(r.created_at).toLocaleString(localeTag)}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{r.event_type}</TableCell>
                        <TableCell>
                          <Badge variant={r.environment === "live" ? "default" : "outline"}>{r.environment}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={r.status === "failed" ? "destructive" : r.status === "processed" ? "secondary" : "default"}
                          >
                            {r.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{r.attempts}</TableCell>
                        <TableCell className="max-w-[280px]">
                          <div className="line-clamp-2 text-xs text-muted-foreground">{r.error ?? "-"}</div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="outline" onClick={() => setSelected(r)}>{t("pages.admin.details")}</Button>
                            {r.status !== "processed" && (
                              <Button size="sm" disabled={retrying === r.id} onClick={() => retry(r.id)}>
                                <RotateCcw className="mr-1 h-3 w-3" />{retrying === r.id ? "…" : t("pages.admin.retry")}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {rows.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">{t("pages.admin.noEvents")}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </PageBody>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selected?.event_type} <span className="ml-2 text-xs text-muted-foreground">{selected?.event_id}</span></DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">{t("pages.admin.webhooksPage.envBadge", { env: selected.environment })}</Badge>
                <Badge variant={selected.status === "failed" ? "destructive" : "secondary"}>{t("pages.admin.webhooksPage.statusBadge", { status: selected.status })}</Badge>
                <Badge variant="outline">{t("pages.admin.webhooksPage.attemptsBadge", { n: selected.attempts })}</Badge>
              </div>
              {selected.error && (
                <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
                  {selected.error}
                </div>
              )}
              <pre className="max-h-[50vh] overflow-auto rounded-md bg-muted p-3 text-[11px]">
{JSON.stringify(selected.payload, null, 2)}
              </pre>
              {selected.status !== "processed" && (
                <Button onClick={() => retry(selected.id)} disabled={retrying === selected.id}>
                  <RotateCcw className="mr-2 h-4 w-4" />{t("pages.admin.webhooksPage.retryManual")}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
